import { createBrowserRouter, Route, Link, NavLink, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';

//pages
import Home, { randomTopMinifiguresLoader } from './pages/Home';
import AllResultsPage, { topMinifiguresLoader } from './pages/AllResultsPage';
import ResultsPage, { topMinifiguresByThemeLoader } from './pages/ResultsPage';
import MinifigurePage from './pages/MinifigurePage';
import CharacterPage from './pages/CharacterPage';
import RankingPage from './pages/RankingPage';
import AnimalRankingPage from './pages/AnimalRankingPage';
import AllAnimalResultsPage, { topAnimalsLoader } from './pages/AllAnimalResultsPage';
import AnimalResultsPage, { topAnimalsByThemeLoader } from './pages/AnimalResultsPage';
import ThemePage from './pages/ThemePage';
import BlogPage from './pages/BlogPage';

//layouts
import RootLayout from './layouts/RootLayout';
import RankingLayout from './layouts/RankingLayout';
import AnimalRankingLayout from './layouts/AnimalRankingLayout';
import ResultsLayout from './layouts/ResultsLayout';
import AnimalResultsLayout from './layouts/AnimalResultsLayout';
import ThemesLayout, {averageRatingsLoader} from './layouts/ThemesLayout';
import CompareLayout from './layouts/CompareLayout';
import BlogLayout from './layouts/BlogLayout';
import MinifigureError from './components/MinifigureError';
import NotFound from './components/NotFound';

function App() {
  const [themesData, setThemesData] = useState([]);
  const [animalThemesData, setAnimalThemesData] = useState([]);

  //The themesLoader is used to get the list of themes for use in the navigation bar on the results page (i.e. ResultsLayout component)
  useEffect(() => {
    const fetchData = async () => {
      const data = await themesLoader();
      setThemesData(data);
    };

    fetchData();
  }, []);

  // Load animal themes data
  useEffect(() => {
    const fetchAnimalThemes = async () => {
      const data = await animalThemesLoader();
      setAnimalThemesData(data);
    };

    fetchAnimalThemes();
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<RootLayout/>}>
          <Route index element={<Home/>} loader={randomTopMinifiguresLoader}/>

          <Route path="ranking" element={<RankingLayout themesData={themesData}/>} errorElement={<MinifigureError/>}>
              <Route path=":themeUrl" element={<RankingPage themesData={themesData}/>}/>
          </Route>

          <Route path="ranking/animals" element={<AnimalRankingLayout animalThemesData={animalThemesData}/>} errorElement={<MinifigureError/>}>
              <Route path=":themeUrl" element={<AnimalRankingPage animalThemesData={animalThemesData}/>}/>
          </Route>

          <Route path="results" element={<ResultsLayout themesData={themesData}/>}>
              <Route index element={<AllResultsPage/>} loader={topMinifiguresLoader}/>
              <Route path="minifigures/:searchValue" element={<CharacterPage/>}/>
              <Route path=":themeUrl" element={<ResultsPage themesData={themesData}/>} loader={topMinifiguresByThemeLoader}>
                  <Route path="minifigures/:searchValue" element={<MinifigurePage/>}/>
              </Route>
          </Route>

          <Route path="results/animals" element={<AnimalResultsLayout animalThemesData={animalThemesData}/>}>
              <Route index element={<AllAnimalResultsPage/>} loader={topAnimalsLoader}/>
              <Route path=":themeUrl" element={<AnimalResultsPage animalThemesData={animalThemesData}/>} loader={topAnimalsByThemeLoader}/>
          </Route>

          <Route path="themes" element={<ThemesLayout themesData={themesData}/>} loader={averageRatingsLoader}>
            <Route path=":themeUrl" element={<ThemePage themesData={themesData}/>}/>
          </Route>

          <Route path="compare" element={<CompareLayout/>}/>

          <Route path="blog" element={<BlogLayout />}>
            <Route path=":slug" element={<BlogPage />} />
          </Route>

          <Route path="*" element={<NotFound/>}/>
      </Route>
    )
  )

  return (
    <HelmetProvider>
      <div className="App">
        <RouterProvider router={router} />
      </div>
    </HelmetProvider>
  );
}

export default App;

export const themesLoader = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const response = await fetch(`${apiUrl}/getThemes.php`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
};

export const animalThemesLoader = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const response = await fetch(`${apiUrl}/getAnimalThemes.php`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
};
